import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
} from "@chakra-ui/modal";
import api from "api/axios";
import Loader from "components/Loader";
import GenericTable from "components/table";
import React, { useState, useRef, useCallback, useEffect } from "react";
import { handleAxiosAndCaughtError } from "utils";
import { EvaluationResultInfo, EvaluationResultItem } from "types";
import { ColumnDef } from "@tanstack/react-table";
import { useAgentInfo } from "layouts/agent/agent-info/_context/AgentInfoContext";
import { useTranslation } from "hooks/useTranslation";

export const HistoryRecordDetailDrawer = ({
  id,
  onClose,
}: {
  id: string | null;
  onClose: () => void;
}) => {
  const { agentInfo } = useAgentInfo();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<EvaluationResultInfo>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    setData(null);
    const controller = new AbortController();
    abortControllerRef.current = controller;
    try {
      const resp = await api(
        `/agents/${agentInfo.id}/evaluations/results/${id}`,
        {
          signal: abortControllerRef.current.signal,
        }
      );

      setData(resp.data.results);
    } catch (error) {
      setError(t("agents.evaluateSection.somethingWentWrong"));
      handleAxiosAndCaughtError(error);
    } finally {
      setLoading(false);
    }
  }, [id, agentInfo]);

  useEffect(() => {
    if (id) fetchData();
    return () => {
      abortControllerRef.current?.abort();
    };
  }, [id, fetchData]);

  let content = null;
  if (loading) {
    content = (
      <Loader
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        txt={t("agents.evaluateSection.loadingResults")}
      />
    );
  } else if (error) {
    content = (
      <div className="absolute left-1/2 top-1/2 flex min-h-[200px] -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center">
        <span className="mb-2 text-red-500">{error}</span>
        <button
          className="mt-2 rounded bg-blue-500 px-4 py-2 text-white"
          onClick={fetchData}
        >
          {t("library.retry")}
        </button>
      </div>
    );
  } else if (data) {
    const columns: ColumnDef<EvaluationResultItem, any>[] = [
      {
        accessorKey: "question",
        header: () => <p>{t("agents.evaluateSection.userQuestion")}</p>,
        cell: (info: { getValue: () => string }) => (
          <p className="text-sm font-bold text-navy-700 dark:text-white">
            {info.getValue()}
          </p>
        ),
      },
      {
        accessorKey: "expected_answer",
        header: () => <p>{t("agents.evaluateSection.expectedAnswer")}</p>,
        cell: (info: { getValue: () => string }) => (
          <p className="text-sm text-navy-700 dark:text-white">
            {info.getValue()}
          </p>
        ),
      },
      {
        accessorKey: "agent_answer",
        header: () => <p>{t("agents.evaluateSection.aiAnswer")}</p>,
        cell: (info: { getValue: () => string }) => (
          <p className="text-sm text-navy-700 dark:text-white">
            {info.getValue()}
          </p>
        ),
      },
      {
        accessorKey: "score",
        header: () => <p>{t("agents.evaluateSection.score")}</p>,
        cell: (info: { getValue: () => number }) => {
          const value = info.getValue();
          let colorClass = "";
          if (value < 0.2) {
            colorClass = "bg-red-100 text-red-800";
          } else if (value < 0.8) {
            colorClass = "bg-blue-100 text-blue-800";
          } else {
            colorClass = "bg-green-100 text-green-800";
          }
          return (
            <span
              className={`rounded-full px-2 py-1 text-xs font-bold ${colorClass}`}
            >
              {value}
            </span>
          );
        },
      },
    ];
    content = (
      <div className="mt-4 flex flex-col">
        <div className="flex items-center justify-between rounded-lg bg-gray-50 px-4 py-3 dark:bg-gray-900">
          <span className="text-base font-semibold text-gray-700 dark:text-gray-200">
            {t("agents.evaluateSection.accuracy")}
          </span>
          <span className="rounded-full bg-green-100 px-3 py-1 text-sm font-bold text-green-800 dark:bg-green-900 dark:text-green-200">
            {data.accuracy ? data.accuracy : "-"}
          </span>
        </div>
        <GenericTable<EvaluationResultItem>
          data={data.results}
          columns={columns}
          searchPlaceholder={t("agents.evaluateSection.searchQuestions")}
        />
      </div>
    );
  }

  return (
    <Drawer
      isOpen={!!id}
      onClose={onClose}
      placement={document.documentElement.dir === "rtl" ? "left" : "right"}
    >
      <div
        className={`
              bg-black/60 dark:bg-black/80 fixed inset-0 backdrop-blur-sm
              transition-opacity duration-300 ease-out
            `}
      />
      <DrawerContent className="my-4 ml-0 mr-4 w-[calc(100vw_-_32px)] max-w-[calc(100vw_-_32px)] rounded-2xl bg-white shadow-[-20px_17px_40px_4px_rgba(112,_144,_176,_0.18)] dark:bg-navy-800 dark:shadow-[-22px_32px_51px_4px_#0B1437] sm:ml-4 md:w-[900px] md:max-w-[900px]">
        <DrawerHeader px="28px" pt="24px" pb="0px">
          <div className="flex items-center justify-between">
            <p className="text-xl font-bold text-gray-900 dark:text-white">
              {t("agents.evaluateSection.evaluationDetails")}
            </p>
          </div>
        </DrawerHeader>
        <DrawerCloseButton className="absolute right-[26px] top-[16px] h-4 w-4 text-gray-900 dark:text-white" />
        <DrawerBody px="28px" pt="0px" pb="24px">
          {content}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
